<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { onBeforeRouteUpdate } from 'vue-router'
import { MOBILEIDENAVIGATIONIDS } from '@/utils/ide'
import { useNavStore } from '@/stores/nav.store'
import { useAuthStore } from '@/stores/auth.store'
import { useOrganisationStore } from '@/stores/organisation.store'

import ProfilePictureComp from '@/components/header/auth/profile/ProfilePictureComp.vue'
import LogoutComp from '@/components/header/auth/profile/LogoutComp.vue'

import StartCodingButton from '@/components/ide/ide/ai/launchPage/StartCodingButton.vue'
import UserIcon from '@/assets/images/svg/profile-menu/user.svg?component'
import DashboardIcon from '@/assets/images/svg/profile-menu/dashboard.svg?component'
import CreditCardIcon from '@/assets/images/svg/profile-menu/credit-card.svg?component'

const authStore = useAuthStore()
const organisationStore = useOrganisationStore()
const navStore = useNavStore()

const viewOrganisations = ref<boolean>(false)
const dropdownItem = ref<HTMLDivElement | null>(null)

const isCollapsedHeader = computed(() => {
  return navStore.isCollapsedHeader
})

const showPersonalProfilePic = computed(() => {
  return organisationStore.activeAccountId == organisationStore.personalAccountId
})

const currentAccountName = computed(() => {
  return authStore.firstName
})

// Roles
const showPlansPayment = computed(() => {
  return (
    organisationStore.isOrganisationOwner ||
    organisationStore.isIndividualAccount ||
    organisationStore.isOrganisationAdmin
  )
})

const showAssessmentsManagement = computed(() => {
  return (
    organisationStore.isOrganisationOwner ||
    organisationStore.isOrganisationAdmin ||
    organisationStore.isTeacher ||
    organisationStore.isInstituteAdmin
  )
})

const showStudentAssessment = computed(() => {
  return organisationStore.isStudent
})

const showApi = computed(() => {
  return (
    organisationStore.isOrganisationOwner ||
    organisationStore.isOrganisationAdmin ||
    organisationStore.isApiAdmin ||
    organisationStore.isApiViewer ||
    organisationStore.isIndividualAccount
  )
})

const showEmbed = computed(() => {
  return (
    organisationStore.isOrganisationOwner ||
    organisationStore.isOrganisationAdmin ||
    organisationStore.isEmbedAdmin ||
    organisationStore.isEmbedViewer ||
    organisationStore.isIndividualAccount
  )
})

const showTeams = computed(() => {
  return (
    organisationStore.isOrganisationOwner ||
    organisationStore.isOrganisationAdmin ||
    organisationStore.isTeacher
  )
})

/**
 * @param event Click event
 * If clicking outside of dropdown div, dropdown will disappear
 */
const clickEventHandler = (event: any) => {
  if (navStore.showMobileProfileNav) {
    if (
      event.target.id != MOBILEIDENAVIGATIONIDS.PROFILETAB &&
      event.target.id != MOBILEIDENAVIGATIONIDS.PROFILEPICID
    ) {
      navStore.setMobileProfileNav(false)
    } else {
      navStore.setMobileProfileNav(true)
    }
  }
}

onMounted(() => {
  document.addEventListener('click', clickEventHandler)
})

onUnmounted(() => {
  document.removeEventListener('click', clickEventHandler)
})

onBeforeRouteUpdate(() => {
  viewOrganisations.value = false
})
</script>

<template>
  <div
    ref="dropdownItem"
    :id="MOBILEIDENAVIGATIONIDS.PROFILETAB"
    class="border-primary surface-primary-base h-full w-full min-w-[13.5rem] border-l p-1 sm:min-w-[15rem]"
  >
    <!-- START User Basic Info -->
    <div class="surface-primary-header mb-0.5 flex items-center gap-2 rounded p-2">
      <ProfilePictureComp
        :show-personal-profile-pic="showPersonalProfilePic"
        :isUserProfileImage="true"
        :currentName="currentAccountName"
        :widthAndHeightClass="'h-10 w-10'"
      />
      <div class="max-w-32 overflow-hidden truncate">
        <h3 class="text-sm font-semibold">{{ currentAccountName }}</h3>
      </div>
    </div>
    <!-- END User Basic Info -->

    <!-- START Menu buttons and links -->
    <div class="flex flex-col">
      <!-- Only when individual profile -->
      <RouterLink
        class="text-button-secondary hover:surface-primary-header hover:text-high flex items-center gap-5 rounded px-2 py-1.5 text-sm font-medium lg:gap-3"
        to="/profile"
      >
        <UserIcon /> Profile
      </RouterLink>
      <hr class="stroke-primary-default my-0.5" />
      <!-- Start Dashboard -->
      <div>
        <RouterLink
          class="text-button-secondary hover:surface-primary-header hover:text-high flex items-center gap-5 rounded px-2 py-1.5 text-sm font-medium lg:gap-3"
          to="/dashboard"
        >
          <DashboardIcon /> Dashboard
        </RouterLink>

        <div>
          <StartCodingButton :isProfileMenu="true" />
          <RouterLink
            class="text-button-secondary hover:surface-primary-header hover:text-high block rounded py-1.5 pl-[3.25rem] text-sm font-medium"
            to="/chat-jdroid"
          >
            Chat JDroid
          </RouterLink>
          <RouterLink
            v-if="showAssessmentsManagement || showStudentAssessment"
            class="text-button-secondary hover:surface-primary-header hover:text-high block rounded py-1.5 pl-[3.25rem] text-sm font-medium"
            :to="`/${showStudentAssessment ? 'student-assessments' : 'assessments'}`"
          >
            Assessments
          </RouterLink>
          <RouterLink
            v-if="showEmbed"
            class="text-button-secondary hover:surface-primary-header hover:text-high block rounded py-1.5 pl-[3.25rem] text-sm font-medium"
            to="/embed-manager"
          >
            Embed
          </RouterLink>
          <RouterLink
            v-if="showApi"
            class="text-button-secondary hover:surface-primary-header hover:text-high block rounded py-1.5 pl-[3.25rem] text-sm font-medium"
            to="/subscribe-api"
          >
            API
          </RouterLink>
          <RouterLink
            v-if="showTeams || showPlansPayment"
            class="text-button-secondary hover:surface-primary-header hover:text-high block rounded py-1.5 pl-[3.25rem] text-sm font-medium"
            :to="organisationStore.isTeacher ? `/teams/student-groups` : `/teams/users`"
          >
            Manage Teams
          </RouterLink>
          <hr class="stroke-primary-default my-0.5" />
        </div>
      </div>

      <RouterLink
        v-if="showPlansPayment"
        class="text-button-secondary hover:surface-primary-header hover:text-high flex items-center gap-5 rounded px-2 py-1.5 text-sm font-medium lg:gap-3"
        to="/plans-payments"
      >
        <CreditCardIcon />
        Plans & Payments
      </RouterLink>

      <LogoutComp v-if="isCollapsedHeader" />
    </div>
    <!-- END Menu buttons and links -->
  </div>
</template>
